import { getConfig } from "@nitro-ape/shared/features/config/api/get-config";
import {
  brandConfig,
  BrandMessageIds,
  buildBrandOgImageUrl,
} from "@nitro-ape/shared/lib/brand-config";
import { getEnv } from "@/lib/env";
import { i18n as i18nGlobal } from "@/lib/i18n";
import { Pool, PoolSchema } from "@nitro-ape/shared/models/pool";
import { createFileRoute, useParams } from "@tanstack/react-router";
import TradeMobileButtons from "./-components/TradeMobileButtons";
import { PoolIdProvider } from "@nitro-ape/shared/providers/pool-id-provider";
import { isServer } from "@tanstack/react-query";
import { getPoolQueryKey } from "@nitro-ape/shared/features/pools/api/get-pool-api";
import { Chain, isChainIndexed } from "@nitro-ape/shared/models/enums/chain";
import TradingResizableSection from "@/components/trading/TradingResizableSection";
import TradePageProviders from "./-trade-page-providers";
import { TradeSidebar } from "../../../components/trading/Sidebar/TradeSidebar";

export const Route = createFileRoute("/_main/trade/$chain/$address")({
  component: TradePage,
  loader: async ({ params, context }) => {
    const { address, chain } = params;
    if (!isChainIndexed(chain as Chain)) {
      return { pool: null, config: null };
    }

    if (!isServer) {
      context.queryClient.refetchQueries({
        queryKey: getPoolQueryKey(chain as Chain, address),
      });
      return { pool: null, config: null };
    }

    const API_BASE_URL = getEnv.VITE_API_URL || "http://localhost:3005/api/v1";

    try {
      const [res, config] = await Promise.all([
        fetch(`${API_BASE_URL}/pools/private/${address}?chain=${chain}`, {
          headers: { "Api-Key": getEnv.BACKEND_API_KEY! },
          cache: "no-store",
        }),
        getConfig(),
      ]);

      if (!res.ok) {
        throw new Error(`Failed to fetch pool: ${res.status}`);
      }
      const json = (await res.json()) as Pool;
      const pool = PoolSchema.parse(json);
      return { pool, config };
    } catch {
      return { pool: null, config: null };
    }
  },

  head: async ({ loaderData }) => {
    const { pool, config } = loaderData!;
    const siteUrl = brandConfig(getEnv).siteUrl;
    const metaI18n = i18nGlobal;
    const t = (id: string) => metaI18n._(id);
    const siteName = t(BrandMessageIds.siteName);

    if (!pool || !config) {
      const title = t(BrandMessageIds.tradeMetaFallbackTitle);
      const description = t(BrandMessageIds.tradeMetaFallbackDescription);
      const fallbackImage = buildBrandOgImageUrl(getEnv);
      const pageUrl = `${siteUrl}/trade`;
      return {
        meta: [
          { title },
          { name: "description", content: description },
          { property: "og:title", content: title },
          { property: "og:description", content: description },
          { property: "og:type", content: "website" },
          { property: "og:site_name", content: siteName },
          { property: "og:locale", content: "en_US" },
          { property: "og:url", content: pageUrl },
          { property: "og:image", content: fallbackImage },
          { name: "twitter:card", content: "summary_large_image" },
          { name: "twitter:title", content: title },
          { name: "twitter:description", content: description },
          { name: "twitter:image", content: fallbackImage },
        ],
      };
    }
    const token = pool.tokens.base;
    const dex = config?.dexes.find((d) => d.protocols.includes(pool.protocol));
    const dexName = dex?.name ?? "DEX";
    const title = metaI18n._({
      id: BrandMessageIds.tradeMetaPoolTitle,
      values: { symbol: token.symbol, dexName },
    });
    const description = metaI18n._({
      id: BrandMessageIds.tradeMetaPoolDescription,
      values: { tokenName: token.name, symbol: token.symbol, dexName },
    });
    const encodedChain = encodeURIComponent(pool.chain);
    const encodedAddress = encodeURIComponent(pool.id);
    const imageUrl = `${siteUrl}/og/trade/${encodedChain}/${encodedAddress}/png`;
    const pageUrl = `${siteUrl}/trade/${pool.chain}/${pool.id}`;
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:image", content: imageUrl },
        { property: "og:image:width", content: "734" },
        { property: "og:image:height", content: "425" },
        { property: "og:type", content: "website" },
        { property: "og:site_name", content: siteName },
        { property: "og:locale", content: "en_US" },
        { property: "og:url", content: pageUrl },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: imageUrl },
      ],
    };
  },
});

function TradePage() {
  const { chain, address } = useParams({
    from: "/_main/trade/$chain/$address",
  });

  console.log("Params", address);
  // const { data: pool } = usePool();

  // if (!chain || !address) {
  //   return <Navigate to="/pump" replace />;
  // }

  // if (!pool) return <Navigate to="/pump" replace />;
  // if (pool?.migrated_to_id) {
  //   return (
  //     <Navigate
  //       to="/trade/$chain/$address"
  //       params={{ chain, address: pool.migrated_to_id }}
  //       replace
  //     />
  //   );
  // }

  return (
    <PoolIdProvider poolId={address} chain={chain as Chain}>
      <TradePageProviders chain={chain as Chain} identifier={address as string}>
        <TradingResizableSection />
        <div className="md:hidden">
          <TradeMobileButtons />
        </div>
        <div className="hidden md:block">
          <TradeSidebar chain={chain as Chain} />
        </div>
      </TradePageProviders>
    </PoolIdProvider>
  );
}
